import { prototypeRuntimeConfig } from "./prototypeRuntimeConfig";
import { loadTurnstile } from './turnstileLoader';

export interface TurnstileTokenProvider {
	/**
	 * Get a token for the given action. In invisible mode this triggers
	 * the challenge; in visible mode it returns a cached token that was
	 * produced by an already-rendered widget.
	 */
	getToken(action: string): Promise<string>;

	/**
	 * Prefetch (warm) an invisible-mode token for the given action so the
	 * first real getToken() call can consume the cache instead of solving
	 * a fresh 4-6s challenge. A prefetched token is still one-shot: it is
	 * removed before being returned to the caller.
	 */
	prefetchToken?(action: string): void;

	/**
	 * Discard a background prefetch for an action. This is intentionally scoped
	 * to prefetched work: a user-started getToken() challenge must remain owned
	 * by the action that started it.
	 */
	cancelPrefetch?(action: string): void;
}

/**
 * Extended provider for visible/interactive Turnstile widgets. The widget
 * is rendered into a container element and the user must solve the challenge
 * before the submit button is unlocked.
 */
export interface InteractiveTurnstileProvider extends TurnstileTokenProvider {
	/**
	 * Render a visible widget into the given container. The callback fires
	 * when the user solves the challenge (or immediately for managed widgets
	 * that pass without interaction). Call reset() to clear and re-challenge.
	 */
	renderWidget(container: HTMLElement, action: string, callbacks: {
		onSuccess: (token: string) => void;
		onError: () => void;
		onExpired: () => void;
	}): Promise<void>;

	/** Reset the widget, clearing any cached token and re-running the challenge. */
	resetWidget(): void;

	/** Remove the widget from the DOM entirely. */
	removeWidget(): void;

	/** Whether a valid token is currently cached. */
	hasToken(): boolean;
}

export function createTurnstileTokenProvider(
	siteKey: string | null | undefined,
	allowDevelopmentFallback = false,
): TurnstileTokenProvider {
	if (!siteKey) {
		return allowDevelopmentFallback ? new DevTurnstileProvider() : new FailClosedTurnstileProvider();
	}

	return new BrowserTurnstileTokenProvider(siteKey);
}

export function createInteractiveTurnstileProvider(
	siteKey: string | null | undefined,
	allowDevelopmentFallback = false,
): InteractiveTurnstileProvider | undefined {
	if (!siteKey) {
		return allowDevelopmentFallback ? new DevTurnstileProvider() : undefined;
	}

	return new BrowserTurnstileTokenProvider(siteKey);
}

class FailClosedTurnstileProvider implements TurnstileTokenProvider {
	async getToken(): Promise<string> {
		throw new Error('turnstile_not_configured');
	}
}

/**
 * Dev provider: no visible widget, auto-passes with the prototype dev token.
 * Used when TURNSTILE_SECRET_KEY is not configured (local dev / non-cloud).
 */
class DevTurnstileProvider implements InteractiveTurnstileProvider {
	private cachedToken: string | null = null;

	async getToken(_action: string): Promise<string> {
		this.cachedToken = prototypeRuntimeConfig.developmentTurnstileToken;
		return this.cachedToken;
	}

	async renderWidget(_container: HTMLElement, _action: string, callbacks: {
		onSuccess: (token: string) => void;
		onError: () => void;
		onExpired: () => void;
	}): Promise<void> {
		this.cachedToken = prototypeRuntimeConfig.developmentTurnstileToken;
		// Fire asynchronously to mimic real widget behavior.
		setTimeout(() => callbacks.onSuccess(this.cachedToken!), 50);
	}

	resetWidget(): void {
		this.cachedToken = null;
		// Re-fire immediately for dev convenience.
		this.cachedToken = prototypeRuntimeConfig.developmentTurnstileToken;
	}

	removeWidget(): void {
		this.cachedToken = null;
	}

	hasToken(): boolean {
		return this.cachedToken !== null;
	}
}

/**
 * Browser provider: renders a real Cloudflare Turnstile widget.
 * Supports both invisible mode (via getToken) and visible/interactive mode
 * (via renderWidget). The visible widget shows the challenge to the user
 * and produces a token only after the user solves it.
 */
interface InvisibleChallengeRequest {
	action: string;
	prefetch: boolean;
	claimed: boolean;
	promise: Promise<string>;
	resolve: (token: string) => void;
	reject: (error: Error) => void;
	cancel?: () => void;
}

class BrowserTurnstileTokenProvider implements InteractiveTurnstileProvider {
	private widgetId: string | null = null;
	private container: HTMLElement | null = null;
	private invisibleContainer: HTMLElement | null = null;
	private invisibleWidgetContainer: HTMLElement | null = null;
	private widgetSurface: 'visible' | 'invisible' | null = null;
	private focusBeforeInvisibleInteraction: HTMLElement | null = null;
	private cachedToken: string | null = null;
	private cachedAction: string | null = null;
	private renderGeneration = 0;

	/**
	 * Invisible-mode token cache (used by vote/comment paths). A Turnstile
	 * token is single-use, so this cache may only contain a token created by
	 * prefetch and is consumed before it is returned. It must never retain the
	 * token returned by a live getToken() call.
	 */
	private static readonly INVISIBLE_TTL_MS = 240_000;
	private invisibleCache = new Map<string, { token: string; expiresAt: number }>();
	private invisibleQueue: InvisibleChallengeRequest[] = [];
	private activeInvisibleRequest: InvisibleChallengeRequest | null = null;
	private prefetchedRequests = new Map<string, InvisibleChallengeRequest>();

	constructor(private readonly siteKey: string) {}

	async getToken(action: string): Promise<string> {
		// A visible-widget token is a one-shot capability. Consume and destroy
		// its widget before returning so a second submit must execute again.
		if (this.cachedToken && this.cachedAction === action) {
			const token = this.cachedToken;
			this.cachedToken = null;
			this.cachedAction = null;
			this.destroyWidget();
			return token;
		}

		// No cached token. If a visible widget was already rendered via
		// renderWidget(), it means the user hasn't solved the challenge yet.
		// We must NOT create a second widget (it would remove the visible one
		// and create a confusing UX). Instead, reject so the caller can show
		// a "please complete verification" message.
		if (this.widgetId && this.cachedAction === action) {
			return Promise.reject(new Error("turnstile_challenge_pending"));
		}

		// Invisible-mode cache: return a fresh cached token if available.
		const cached = this.invisibleCache.get(action);
		if (cached && cached.expiresAt > Date.now()) {
			this.invisibleCache.delete(action);
			return cached.token;
		}
		if (cached) this.invisibleCache.delete(action); // expired

		// A prefetch represents one future token and may be claimed once. Live
		// calls never share each other's result: they are queued globally and
		// each executes a separate challenge after the preceding one settles.
		const prefetched = this.prefetchedRequests.get(action);
		if (prefetched && !prefetched.claimed) {
			prefetched.claimed = true;
			return prefetched.promise;
		}
		return this.enqueueInvisibleChallenge(action, false);
	}

	/** Prefetch (warm) an invisible token so the first real call is instant. */
	prefetchToken(action: string): void {
		const cached = this.invisibleCache.get(action);
		if (cached && cached.expiresAt > Date.now()) return;
		if (this.prefetchedRequests.has(action)) return;
		// A background prefetch is also serialized through the global FIFO. If
		// a real request arrives before it settles, that request claims this one
		// token exactly once; otherwise it is stored as a one-shot cache entry.
		void this.enqueueInvisibleChallenge(action, true).catch(() => { /* prefetch failure is non-fatal */ });
	}

	/**
	 * Community warm-up is page-scoped. It must never survive a route change or
	 * turn into an interactive overlay while another visible challenge owns the
	 * foreground. Only matching prefetch work is cancelled; live requests keep
	 * their normal one-shot lifecycle.
	 */
	cancelPrefetch(action: string): void {
		this.invisibleCache.delete(action);
		const prefetched = this.prefetchedRequests.get(action);
		// A live getToken() caller can claim an in-flight warm-up. From that
		// point forward it is foreground user work, so route/modal cleanup must
		// not cancel it or suppress the interaction Cloudflare may require.
		if (!prefetched || prefetched.claimed) return;
		this.prefetchedRequests.delete(action);
		this.invisibleQueue = this.invisibleQueue.filter((request) => request !== prefetched);
		if (this.activeInvisibleRequest === prefetched) {
			this.renderGeneration += 1;
			prefetched.cancel?.();
		}
		prefetched.reject(new Error('turnstile_challenge_invalidated'));
	}

	private enqueueInvisibleChallenge(action: string, prefetch: boolean): Promise<string> {
		let resolve!: (token: string) => void;
		let reject!: (error: Error) => void;
		const promise = new Promise<string>((done, fail) => { resolve = done; reject = fail; });
		const request: InvisibleChallengeRequest = { action, prefetch, claimed: false, promise, resolve, reject };
		if (prefetch) this.prefetchedRequests.set(action, request);
		this.invisibleQueue.push(request);
		void this.drainInvisibleQueue();
		return promise;
	}

	/**
	 * Turnstile permits only one rendered challenge per invisible provider. A
	 * single FIFO across every action prevents a same-action or cross-action
	 * render from removing and invalidating the preceding challenge.
	 */
	private async drainInvisibleQueue(): Promise<void> {
		if (this.activeInvisibleRequest) return;
		const request = this.invisibleQueue.shift();
		if (!request) return;
		this.activeInvisibleRequest = request;
		try {
			const token = await this.solveInvisibleChallenge(request);
			if (request.prefetch && !request.claimed) {
				this.invisibleCache.set(request.action, { token, expiresAt: Date.now() + BrowserTurnstileTokenProvider.INVISIBLE_TTL_MS });
			}
			request.resolve(token);
		} catch (error) {
			request.reject(error instanceof Error ? error : new Error('turnstile_challenge_failed'));
		} finally {
			if (request.prefetch && this.prefetchedRequests.get(request.action) === request) this.prefetchedRequests.delete(request.action);
			this.activeInvisibleRequest = null;
			void this.drainInvisibleQueue();
		}
	}

	/**
	 * Solve a fresh invisible Turnstile challenge for the given action.
	 * On success, returns a fresh one-shot token. Only prefetchToken() may
	 * cache a token for a future action.
	 */
	private async solveInvisibleChallenge(request: InvisibleChallengeRequest): Promise<string> {
		const { action } = request;
		const generation = ++this.renderGeneration;
		const turnstile = await loadTurnstile();
		if (this.renderGeneration !== generation) throw new Error('turnstile_challenge_invalidated');
		const container = this.ensureInvisibleContainer();
		this.destroyWidget();

		return new Promise((resolve, reject) => {
			let settled = false;
			let synchronousToken: string | null = null;
			let timeoutHandle: ReturnType<typeof setTimeout> | undefined;
			const isCurrentRender = () => this.renderGeneration === generation;
			const rejectInvalidated = () => {
				if (settled) return;
				settled = true;
				if (timeoutHandle) clearTimeout(timeoutHandle);
				reject(new Error('turnstile_challenge_invalidated'));
			};
			const fail = (errorCode?: string) => {
				if (settled) return;
				if (!isCurrentRender()) return rejectInvalidated();
				settled = true;
				this.destroyWidget();
				reject(new Error(`turnstile_challenge_failed${errorCode ? `:${errorCode}` : ""}`));
			};
			const succeed = (token: string) => {
				if (settled) return;
				if (!isCurrentRender()) return rejectInvalidated();
				if (!this.widgetId) {
					synchronousToken = token;
					return;
				}
				settled = true;
				this.destroyWidget();
				resolve(token);
			};

			// Bound a silent provider failure, but give a real person enough time
			// once Cloudflare has asked them to interact with the challenge.
			const armSafetyTimeout = (timeoutMs: number) => {
				if (timeoutHandle) clearTimeout(timeoutHandle);
				timeoutHandle = setTimeout(() => fail("safety_timeout"), timeoutMs);
			};
			armSafetyTimeout(15_000);
			request.cancel = () => {
				if (settled) return;
				this.destroyWidget();
				rejectInvalidated();
			};

			try {
				this.widgetSurface = 'invisible';
				this.widgetId = turnstile.render(container, {
					sitekey: this.siteKey,
					action,
					size: "normal",
					// Managed widgets stay visually silent for automatic passes.
					// When Cloudflare needs a person to interact, its documented
					// interaction-only mode invokes the callbacks below and exposes
					// a real, focusable overlay instead of leaving the challenge in
					// an inaccessible 1px container.
					appearance: "interaction-only",
					"before-interactive-callback": () => {
						if (settled || !isCurrentRender()) return;
						// A warm-up must stay silent. If Cloudflare raises its risk
						// assessment, discard it and let the eventual user action own
						// any required interaction instead of stealing the foreground.
						if (request.prefetch && !request.claimed) {
							this.renderGeneration += 1;
							request.cancel?.();
							return;
						}
						this.setInvisibleInteractionActive(true);
						armSafetyTimeout(120_000);
					},
					"after-interactive-callback": () => {
						if (settled || !isCurrentRender()) return;
						this.setInvisibleInteractionActive(false);
					},
					callback: (token: string) => {
						clearTimeout(timeoutHandle);
						succeed(token);
					},
					"error-callback": (errorCode?: string) => {
						clearTimeout(timeoutHandle);
						fail(errorCode);
						return true;
					},
					"expired-callback": () => {
						clearTimeout(timeoutHandle);
						fail("expired");
					},
					"timeout-callback": () => {
						clearTimeout(timeoutHandle);
						fail("challenge_timeout");
					},
				});
				if (settled) {
					this.destroyWidget();
					return;
				}
				if (synchronousToken) succeed(synchronousToken);
			} catch (err) {
				clearTimeout(timeoutHandle);
				fail("render_error");
			}
		});
	}

	async renderWidget(container: HTMLElement, action: string, callbacks: {
		onSuccess: (token: string) => void;
		onError: () => void;
		onExpired: () => void;
	}): Promise<void> {
		const generation = ++this.renderGeneration;
		const turnstile = await loadTurnstile();
		if (this.renderGeneration !== generation) throw new Error('turnstile_render_invalidated');
		const isCurrentRender = () => this.renderGeneration === generation;

		// Clear any previous widget.
		this.destroyWidget();
		this.cachedToken = null;
		this.cachedAction = action;
		this.container = container;
		this.widgetSurface = 'visible';

		// Clear the container in case there's leftover markup.
		container.innerHTML = "";

		this.widgetId = turnstile.render(container, {
			sitekey: this.siteKey,
			action,
			size: "normal",
			callback: (token: string) => {
				if (!isCurrentRender()) return;
				this.cachedToken = token;
				callbacks.onSuccess(token);
			},
			"error-callback": (errorCode?: string) => {
				if (!isCurrentRender()) return true;
				this.cachedToken = null;
				callbacks.onError();
				return true;
			},
			"expired-callback": () => {
				if (!isCurrentRender()) return;
				this.cachedToken = null;
				callbacks.onExpired();
			},
			"timeout-callback": () => {
				if (!isCurrentRender()) return;
				this.cachedToken = null;
				callbacks.onError();
			},
		});
	}

	resetWidget(): void {
		this.cachedToken = null;
		if (this.widgetId && window.turnstile) {
			try { window.turnstile.reset(this.widgetId); } catch { /* ignore */ }
		}
	}

	removeWidget(): void {
		this.renderGeneration += 1;
		this.cachedToken = null;
		this.cachedAction = null;
		this.destroyWidget();
	}

	hasToken(): boolean {
		return this.cachedToken !== null;
	}

	private ensureInvisibleContainer(): HTMLElement {
		if (this.invisibleContainer?.isConnected && this.invisibleWidgetContainer) return this.invisibleWidgetContainer;
		const container = document.createElement("div");
		container.id = "deepmox-turnstile";
		container.setAttribute("aria-hidden", "true");
		container.setAttribute("role", "dialog");
		container.setAttribute("aria-label", "Security verification");
		container.tabIndex = -1;
		container.style.position = "fixed";
		container.style.inset = "0";
		container.style.zIndex = "2147483647";
		container.style.display = "flex";
		container.style.alignItems = "center";
		container.style.justifyContent = "center";
		container.style.opacity = "0";
		container.style.pointerEvents = "none";
		container.style.background = "transparent";
		container.style.transition = "opacity 120ms ease";
		const widgetContainer = document.createElement("div");
		widgetContainer.style.display = "flex";
		widgetContainer.style.justifyContent = "center";
		widgetContainer.style.minWidth = "300px";
		container.appendChild(widgetContainer);
		document.body.appendChild(container);
		this.invisibleContainer = container;
		this.invisibleWidgetContainer = widgetContainer;
		return widgetContainer;
	}

	private setInvisibleInteractionActive(active: boolean): void {
		const container = this.invisibleContainer;
		if (!container?.isConnected) return;
		container.setAttribute("aria-hidden", active ? "false" : "true");
		container.setAttribute("aria-modal", active ? "true" : "false");
		container.style.opacity = active ? "1" : "0";
		container.style.pointerEvents = active ? "auto" : "none";
		container.style.background = active ? "rgba(15, 23, 42, 0.52)" : "transparent";
		if (active) {
			this.focusBeforeInvisibleInteraction = document.activeElement instanceof HTMLElement ? document.activeElement : null;
			container.focus({ preventScroll: true });
		} else if (container.contains(document.activeElement)) {
			const restoreTarget = this.focusBeforeInvisibleInteraction;
			if (restoreTarget?.isConnected && restoreTarget !== container) restoreTarget.focus({ preventScroll: true });
			else container.blur();
			this.focusBeforeInvisibleInteraction = null;
		}
	}

	private destroyWidget(): void {
		const widgetId = this.widgetId;
		this.widgetId = null;
		if (widgetId && window.turnstile) {
			try { window.turnstile.remove(widgetId); } catch { /* ignore */ }
		}
		const surface = this.widgetSurface;
		this.widgetSurface = null;
		if (surface === 'invisible') {
			this.setInvisibleInteractionActive(false);
			if (this.invisibleWidgetContainer?.isConnected) this.invisibleWidgetContainer.innerHTML = '';
		} else if (surface === 'visible' && this.container?.isConnected) {
			this.container.innerHTML = '';
		}
	}
}
